import React, {useMemo} from 'react';
import TextField from '../../../components/TextField/TextField';
import SelectField from '../../../components/SelectField/SelectField';
import CustomButton from '../../../components/Button/Button';
import {useTranslation} from 'react-i18next';
import {useFormik} from 'formik';
import {makeStyles} from '@material-ui/core/styles';
import {AccountsListFilters} from '../../../store/actions/accounts/payloads';

const useStyles = makeStyles(() => ({
    filtersContainer: {
        flex: 1,
        marginTop: 20,
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '16px 0px',
    },
    filtersInputs: {
        display: 'flex',
        '& MuiFormControl-root': {
            flex: 1,
            maxWidth: 400,
        },
    },
    filterFieldNarrow: {
        marginRight: 18,
        flex: 1,
        minWidth:172,
        maxWidth: 240,
        background: 'white',
    },
    // Wider than the other filters so the "Active & not yet active" option
    // fits on a single line (the popper inherits the anchor width).
    statusFilterField: {
        marginRight: 18,
        flex: 1,
        minWidth: 172,
        maxWidth: 300,
        background: 'white',
    },
}));

interface FiltersProps {
    page: number;
    pageSize: number;
    handleSearch: (params: {pageSize: number; page: number}, filters: AccountsListFilters) => void;
    handleForceFirstPage?: () => void;
}

const FiltersComponent: React.VFC<FiltersProps> = ({page, pageSize, handleSearch, handleForceFirstPage}) => {
    const {t} = useTranslation();
    const classes = useStyles();

    const statusOptions = useMemo(
        () => [
            {value: '', label: t<string>('screens:accounts.anyStatus')},
            {value: 'O', label: t<string>('screens:accounts.statusActiveAndNotYetActive')},
            {value: 'S', label: t<string>('screens:accounts.suspended')},
            {value: 'I', label: t<string>('screens:accounts.inactive')},
        ],
        [t],
    );

    const {values, handleChange, handleSubmit, setFieldValue} = useFormik<AccountsListFilters>({
        initialValues: {
            idFilter: '',
            emailFilter: '',
            cityFilter: '',
            billStatusFilter: '',
        },
        onSubmit: (form) => {
            handleSearch({page: 0, pageSize}, form);
            handleForceFirstPage?.();
        },
    });

    return (
        <form
            className={classes.filtersContainer}
            onSubmit={handleSubmit}
            data-qa="accounts-list-filters"
        >
            <div className={classes.filtersInputs}>
                <TextField
                    id="idFilter"
                    className={classes.filterFieldNarrow}
                    label={t('screens:accounts.accountId')}
                    value={values.idFilter}
                    onChange={handleChange}
                    dataQa="filter-by-id"
                />

                <TextField
                    id="emailFilter"
                    className={classes.filterFieldNarrow}
                    label={t('common:emailAddress')}
                    value={values.emailFilter}
                    onChange={handleChange}
                    dataQa="filter-by-email"
                />

                <TextField
                    id="cityFilter"
                    className={classes.filterFieldNarrow}
                    label={t('screens:accounts.city')}
                    value={values.cityFilter}
                    onChange={handleChange}
                    dataQa="filter-by-city"
                />

                <SelectField
                    id="billStatusFilter"
                    className={classes.statusFilterField}
                    label={t('common:status')}
                    items={statusOptions}
                    getOptionLabel={(o) => o.label}
                    value={statusOptions.find((o) => o.value === values.billStatusFilter) ?? statusOptions[0]}
                    onChange={(_, value) => setFieldValue('billStatusFilter', (value as typeof statusOptions[0])?.value ?? '')}
                    dataQa="filter-by-status"
                    disableClearable
                />
            </div>

            <CustomButton dataQa="search-button" primary accent type="submit">
                {t('common:search')}
            </CustomButton>
        </form>
    );
};

export default FiltersComponent;
